// ──────────────────────────────────────────────────────────
// hooks/useStrategyDashboard.js (SSE version)
//
// NO POLLING. Receives real-time events through your
// existing SSE connection (/api/live/feed).
//
// Events listened:
//   strategy:signal  → signal fired
//   strategy:trade   → trade opened / closed / trailed
//   strategy:pnl     → live P&L update
//   strategy:summary → summary stats refresh
//
// Initial load: one GET /api/strategy/dashboard on mount.
// After that, everything is pushed via SSE.
// ──────────────────────────────────────────────────────────

// import { useState, useEffect, useCallback, useRef } from "react";

// const BASE_URL =
//   process.env.REACT_APP_LIVE_URL || process.env.REACT_APP_BASE_URL;

// export function useStrategy() {
//   const [openTrades, setOpenTrades] = useState([]);
//   const [closedTrades, setClosedTrades] = useState([]);
//   const [signals, setSignals] = useState([]);
//   const [summary, setSummary] = useState({});
//   const [settings, setSettings] = useState({ auto_trade_enabled: false });
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const eventSourceRef = useRef(null);
//   const userIdRef = useRef(null);

//   // ── Initial load (one-time REST call) ──────────────────

//   const fetchInitial = useCallback(async () => {
//     try {
//       const res = await fetch(`${BASE_URL}/api/strategy/dashboard`);
//       const json = await res.json();

//       if (json.success) {
//         const d = json.data;
//         setOpenTrades(d.openTrades || []);
//         setClosedTrades(d.closedTrades || []);
//         setSignals(d.signals || []);
//         setSummary(d.summary || {});
//         setSettings(d.settings || {});
//         setError(null);
//       }
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   // ── Connect to your existing SSE ───────────────────────

//   useEffect(() => {
//     // Initial data load
//     fetchInitial();

//     // Connect to same SSE endpoint your app already uses
//     const es = new EventSource(`${BASE_URL}/api/live/feed`);
//     eventSourceRef.current = es;

//     es.addEventListener("connected", (e) => {
//       try {
//         const data = JSON.parse(e.data);
//         userIdRef.current = data.userId;
//       } catch (_) {}
//     });

//     // ── Strategy events ──────────────────────────────────

//     es.addEventListener("strategy:signal", (e) => {
//       try {
//         const signal = JSON.parse(e.data);

//         setSignals((prev) => [signal, ...prev].slice(0, 50));
//       } catch (_) {}
//     });

//     es.addEventListener("strategy:trade", (e) => {
//       try {
//         const { action, trade } = JSON.parse(e.data);

//         if (action === "OPENED") {
//           setOpenTrades((prev) => [trade, ...prev]);
//         }

//         if (action === "CLOSED") {
//           // Remove from open, add to closed
//           setOpenTrades((prev) => prev.filter((t) => t.id !== trade.id));
//           setClosedTrades((prev) => [trade, ...prev]);
//         }

//         if (action === "TRAILED") {
//           // Update the open trade in place
//           setOpenTrades((prev) =>
//             prev.map((t) =>
//               t.id === trade.id
//                 ? {
//                     ...t,
//                     stop_loss: trade.stop_loss,
//                     trailing_active: true,
//                     pnl_points: trade.pnl_points,
//                     pnl_amount: trade.pnl_amount,
//                   }
//                 : t,
//             ),
//           );
//         }
//       } catch (_) {}
//     });

//     es.addEventListener("strategy:pnl", (e) => {
//       try {
//         const { tradeId, pnlPoints, pnlAmount } = JSON.parse(e.data);

//         setOpenTrades((prev) =>
//           prev.map((t) =>
//             t.id === tradeId
//               ? { ...t, pnl_points: pnlPoints, pnl_amount: pnlAmount }
//               : t,
//           ),
//         );
//       } catch (_) {}
//     });

//     es.addEventListener("strategy:summary", (e) => {
//       try {
//         const data = JSON.parse(e.data);
//         setSummary(data);
//       } catch (_) {}
//     });

//     es.onerror = () => {
//       // SSE auto-reconnects, no action needed
//     };

//     return () => {
//       es.close();
//     };
//   }, [fetchInitial]);

//   // ── Actions (REST calls) ───────────────────────────────

//   const toggleAutoTrade = useCallback(async (enabled) => {
//     try {
//       // Optimistic update
//       setSettings((prev) => ({ ...prev, auto_trade_enabled: enabled }));

//       await fetch(`${BASE_URL}/api/strategy/settings`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ auto_trade_enabled: enabled }),
//       });
//     } catch (err) {
//       console.error("Toggle failed:", err.message);
//       // Revert on failure
//       setSettings((prev) => ({ ...prev, auto_trade_enabled: !enabled }));
//     }
//   }, []);

//   const closeTrade = useCallback(async (tradeId, exitPrice) => {
//     try {
//       await fetch(`${BASE_URL}/api/strategy/close-trade`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ tradeId, exitPrice }),
//       });
//       // SSE will push the update, no need to refetch
//     } catch (err) {
//       console.error("Close trade failed:", err.message);
//     }
//   }, []);

//   const closeAll = useCallback(async () => {
//     try {
//       await fetch(`${BASE_URL}/api/strategy/close-all`, {
//         method: "POST",
//       });
//       // SSE will push each close event
//     } catch (err) {
//       console.error("Close all failed:", err.message);
//     }
//   }, []);

//   return {
//     openTrades,
//     closedTrades,
//     signals,
//     summary,
//     settings,
//     loading,
//     error,

//     toggleAutoTrade,
//     closeTrade,
//     closeAll,
//     refresh: fetchInitial,
//   };
// }
import { useState, useEffect, useCallback, useRef } from "react";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export function useStrategy() {
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState({});
  const [settings, setSettings] = useState({ auto_trade_enabled: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const eventSourceRef = useRef(null);
  const userIdRef = useRef(null);

  const fetchInitial = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/strategy/dashboard`);
      const json = await res.json();

      if (json.success) {
        const d = json.data;
        setOpenTrades(d.openTrades || []);
        setClosedTrades(d.closedTrades || []);
        setSignals(d.signals || []);
        setSummary(d.summary || {});
        setSettings(d.settings || {});
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitial();

    const es = new EventSource(`${BASE_URL}/api/live/feed`);
    eventSourceRef.current = es;

    es.addEventListener("connected", (e) => {
      try {
        const data = JSON.parse(e.data);
        userIdRef.current = data.userId;
      } catch (_) {}
    });

    es.addEventListener("strategy:signal", (e) => {
      try {
        const signal = JSON.parse(e.data);
        setSignals((prev) => [signal, ...prev].slice(0, 50));
      } catch (_) {}
    });

    es.addEventListener("strategy:trade", (e) => {
      try {
        const { action, trade } = JSON.parse(e.data);

        if (action === "OPENED") {
          setOpenTrades((prev) => [trade, ...prev]);
        }

        if (action === "CLOSED") {
          setOpenTrades((prev) => prev.filter((t) => t.id !== trade.id));
          setClosedTrades((prev) => [trade, ...prev]);
        }

        if (action === "TRAILED") {
          setOpenTrades((prev) =>
            prev.map((t) =>
              t.id === trade.id
                ? {
                    ...t,
                    stop_loss: trade.stop_loss,
                    trailing_active: true,
                    pnl_points: trade.pnl_points,
                    pnl_amount: trade.pnl_amount,
                  }
                : t,
            ),
          );
        }
      } catch (_) {}
    });

    es.addEventListener("strategy:pnl", (e) => {
      try {
        const { tradeId, pnlPoints, pnlAmount } = JSON.parse(e.data);
        setOpenTrades((prev) =>
          prev.map((t) =>
            t.id === tradeId
              ? { ...t, pnl_points: pnlPoints, pnl_amount: pnlAmount }
              : t,
          ),
        );
      } catch (_) {}
    });

    es.addEventListener("strategy:summary", (e) => {
      try {
        const data = JSON.parse(e.data);
        setSummary(data);
      } catch (_) {}
    });

    es.onerror = () => {};

    return () => {
      es.close();
    };
  }, [fetchInitial]);

  const toggleAutoTrade = useCallback(async (enabled) => {
    try {
      setSettings((prev) => ({ ...prev, auto_trade_enabled: enabled }));
      await fetch(`${BASE_URL}/api/strategy/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auto_trade_enabled: enabled }),
      });
    } catch (err) {
      console.error("Toggle failed:", err.message);
      setSettings((prev) => ({ ...prev, auto_trade_enabled: !enabled }));
    }
  }, []);

  const closeTrade = useCallback(async (tradeId, exitPrice) => {
    try {
      await fetch(`${BASE_URL}/api/strategy/close-trade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tradeId, exitPrice }),
      });
    } catch (err) {
      console.error("Close trade failed:", err.message);
    }
  }, []);

  const closeAll = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/api/strategy/close-all`, { method: "POST" });
    } catch (err) {
      console.error("Close all failed:", err.message);
    }
  }, []);

  return {
    openTrades,
    closedTrades,
    signals,
    summary,
    settings,
    loading,
    error,
    toggleAutoTrade,
    closeTrade,
    closeAll,
    refresh: fetchInitial,
  };
}
